import Papa from 'papaparse'
import {
	createContext,
	useContext,
	useEffect,
	useMemo,
	useReducer,
} from 'react'

import { reducer } from './reducer'
import {
	ActionTypeEnum,
	CsvImporterContextState,
	CsvImporterContextType,
	CsvImporterStepEnum,
	InputDataParsed,
} from './types'

const CsvImporterContext = createContext<CsvImporterContextType | undefined>(
	undefined,
)

CsvImporterContext.displayName = 'CsvImporterContext'

export const useCsvImporterContext = () =>
	useContext(CsvImporterContext) as CsvImporterContextType

type Props = {
	children: React.ReactNode
	initialState: Omit<
		CsvImporterContextState,
		'resetImport' | 'createAnywayTableNames' | 'step' | 'hasHeaders' | 'mapping'
	>
	inputData: string
}

export const CsvImporterContextProvider = ({
	children,
	inputData,
	initialState,
}: Props) => {
	const [state, dispatch] = useReducer(reducer, {
		...initialState,
		resetImport: () => null,
		mapping: {},
		hasHeaders: true,
		step: CsvImporterStepEnum.PREVIEW,
	})

	const context = useMemo<CsvImporterContextType>(
		() => ({
			state: {
				...state,
				resetImport: () => {
					dispatch({
						type: ActionTypeEnum.CHECK_RESULT_SET,
						checkResult: undefined,
					})

					state.onMapped()
				},
			},
			dispatch,
		}),
		[state, dispatch],
	)

	useEffect(() => {
		const inputDataParsed = Papa.parse(inputData) as InputDataParsed

		const inputDataValidated = {
			...inputDataParsed,
			data: inputDataParsed.data.filter((row) => row.length > 1),
		} as InputDataParsed

		dispatch({
			type: ActionTypeEnum.INPUT_DATA_PARSED_SET,
			inputDataParsed: inputDataValidated,
		})
	}, [inputData])

	return (
		<CsvImporterContext.Provider value={context}>
			{children}
		</CsvImporterContext.Provider>
	)
}
